import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import toast from 'react-hot-toast';
import {
  KeyIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

const AVAILABLE_SCOPES = [
  { value: '*', label: 'Full Access', description: 'Access to all API endpoints' },
  { value: 'bots:read', label: 'Bots (Read)', description: 'List and view bots' },
  { value: 'bots:write', label: 'Bots (Write)', description: 'Create and manage bots' },
  { value: 'chat:execute', label: 'Chat', description: 'Start chats and send messages' },
  { value: 'inbox:read', label: 'Inbox (Read)', description: 'View conversations and messages' },
  { value: 'inbox:write', label: 'Inbox (Write)', description: 'Send messages to conversations' },
];

const ApiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(null);

  // Form state
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState(['*']);
  const [expiresInDays, setExpiresInDays] = useState('');

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const res = await api.get('/api-keys');
      setKeys(res.data.keys);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: keyName,
        scopes: selectedScopes,
      };
      if (expiresInDays) {
        payload.expiresInDays = parseInt(expiresInDays);
      }

      const res = await api.post('/api-keys', payload);
      setNewKey(res.data);
      setShowCreateModal(false);
      setShowKeyModal(true);
      setKeyName('');
      setSelectedScopes(['*']);
      setExpiresInDays('');
      fetchKeys();
      toast.success('API key created successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to revoke "${keyName}"? This action cannot be undone.`)) {
      return;
    }
    setRevoking(keyId);
    try {
      await api.delete(`/api-keys/${keyId}`);
      toast.success('API key revoked');
      fetchKeys();
    } catch (error) {
      toast.error('Failed to revoke API key');
    } finally {
      setRevoking(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleScope = (scope) => {
    if (scope === '*') {
      setSelectedScopes(['*']);
      return;
    }
    let newScopes = selectedScopes.filter(s => s !== '*');
    if (newScopes.includes(scope)) {
      newScopes = newScopes.filter(s => s !== scope);
    } else {
      newScopes.push(scope);
    }
    if (newScopes.length === 0) newScopes = ['*'];
    setSelectedScopes(newScopes);
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatScopes = (scopes) => {
    if (!scopes || scopes.includes('*')) return 'Full Access';
    return scopes.join(', ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">API Keys</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Manage API keys for external integrations with your Chatza workspace.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors shadow-sm"
        >
          <PlusIcon className="h-5 w-5" />
          Generate Key
        </button>
      </div>

      {/* API Documentation hint */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <ShieldCheckIcon className="h-5 w-5 text-blue-500 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Public API Endpoints</p>
            <p className="mt-1">
              Use your API key with the base URL: <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs font-mono">{window.location.origin}/api/v1</code>
            </p>
            <p className="mt-1">
              Authenticate via <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs font-mono">Authorization: Bearer czk_xxx</code> or <code className="bg-blue-100 px-1.5 py-0.5 rounded text-xs font-mono">X-API-Key: czk_xxx</code>
            </p>
          </div>
        </div>
      </div>

      {/* Keys Table */}
      {keys.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-neutral-200">
          <KeyIcon className="h-12 w-12 text-neutral-300 mx-auto" />
          <h3 className="mt-3 text-sm font-medium text-neutral-900">No API keys</h3>
          <p className="mt-1 text-sm text-neutral-500">Generate an API key to get started with the Public API.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Scopes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Last Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {keys.map((key) => (
                  <tr key={key.id} className={key.status === 'revoked' ? 'bg-neutral-50 opacity-60' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <KeyIcon className="h-4 w-4 text-neutral-400" />
                        <span className="text-sm font-medium text-neutral-900">{key.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-xs font-mono bg-neutral-100 px-2 py-1 rounded text-neutral-600">
                        {key.key_prefix}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-xs text-neutral-600">{formatScopes(key.scopes)}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        key.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {key.status}
                      </span>
                      {key.expires_at && new Date(key.expires_at) < new Date() && key.status === 'active' && (
                        <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          expired
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-neutral-500">
                      {formatDate(key.last_used_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-neutral-500">
                      {formatDate(key.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {key.status === 'active' && (
                        <button
                          onClick={() => handleRevoke(key.id, key.name)}
                          disabled={revoking === key.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          {revoking === key.id ? 'Revoking...' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-neutral-900">Generate API Key</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1 text-neutral-400 hover:text-neutral-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Key Name</label>
                <input
                  type="text"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  placeholder="e.g., Production Integration"
                  required
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Permissions</label>
                <div className="space-y-2">
                  {AVAILABLE_SCOPES.map((scope) => (
                    <label key={scope.value} className="flex items-start gap-3 p-2 rounded-lg hover:bg-neutral-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedScopes.includes(scope.value)}
                        onChange={() => toggleScope(scope.value)}
                        className="mt-0.5 h-4 w-4 text-primary-500 border-neutral-300 rounded focus:ring-primary-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-neutral-800">{scope.label}</span>
                        <p className="text-xs text-neutral-500">{scope.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Expiration (optional)</label>
                <select
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Never expires</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="180">6 months</option>
                  <option value="365">1 year</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating || !keyName.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50"
                >
                  {creating ? 'Generating...' : 'Generate Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Show Key Modal (one-time display) */}
      {showKeyModal && newKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-neutral-900">API Key Created</h2>
              <button onClick={() => { setShowKeyModal(false); setNewKey(null); }} className="p-1 text-neutral-400 hover:text-neutral-600">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                <p className="text-sm text-yellow-700">
                  <strong>Copy this key now.</strong> You won't be able to see it again.
                </p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 mb-1">{newKey.name}</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-neutral-900 text-green-400 px-4 py-3 rounded-lg text-sm font-mono break-all">
                  {newKey.key}
                </code>
                <button
                  onClick={() => copyToClipboard(newKey.key)}
                  className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? <CheckIcon className="h-5 w-5 text-green-500" /> : <ClipboardDocumentIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="text-xs text-neutral-500 space-y-1 mb-4">
              <p><strong>Scopes:</strong> {formatScopes(newKey.scopes)}</p>
              {newKey.expires_at && <p><strong>Expires:</strong> {formatDate(newKey.expires_at)}</p>}
            </div>

            <div className="bg-neutral-50 rounded-lg p-3 mb-4">
              <p className="text-xs font-medium text-neutral-700 mb-1">Quick test:</p>
              <code className="text-xs font-mono text-neutral-600 break-all">
                curl -H "Authorization: Bearer {newKey.key}" {window.location.origin}/api/v1/bots
              </code>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => { setShowKeyModal(false); setNewKey(null); }}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeys;
